import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import '../shared.css';

const ItemEdit = ({ match }) => {
  const recordId = match.params.id;
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  const [selectedCategory, setSelectedCategory]     = useState('');
  const [selectedPGCategory, setSelectedPGCategory] = useState('');
  const [selectedItemGroup, setSelectedItemGroup]   = useState('');

  const [dropdownCategoryOptions, setDropdownCategoryOptions]     = useState([]);
  const [dropdownPGCategoryOptions, setDropdownPGCategoryOptions] = useState([]);
  const [dropdownItemGroupOptions, setDropdownItemGroupOptions]   = useState([]);

  const [formData, setFormData] = useState({
    Code: '', Name: '', PF: '', ISG: '',
    PurchaseRate: '', MRP: '', HSNCode: '',
    GSTRate: '', DiscountPercentage: '',
    IsExcluding: false, CategoryId: '', PGCategoryId: '', ItemGroupId: '',
  });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
  }, [isAuthenticated, companyId, token, logout]);

  useEffect(() => {
    fetchDropdownCategoryOptions();
    fetchDropdownPGCategoryOptions();
    fetchDropdownItemGroupOptions();
  }, []);

  useEffect(() => {
    fetchData('ItemApi', `/ItemDetails/${recordId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setFormData({ ...response.response_data, UpdatedBy: userId });
          setSelectedCategory(response.response_data.CategoryId);
          setSelectedPGCategory(response.response_data.PGCategoryId);
          setSelectedItemGroup(response.response_data.ItemGroupId);
        }
      })
      .catch(err => console.error('Error fetching Item Details:', err));
  }, []);

  const fetchDropdownCategoryOptions = () => {
    fetchData('ItemApi', `/Category/${companyId}`, token)
      .then(response => setDropdownCategoryOptions(response.response_data))
      .catch(err => console.error('Error fetching Category dropdown:', err));
  };

  const fetchDropdownPGCategoryOptions = () => {
    fetchData('ItemApi', `/PGCategory/${companyId}`, token)
      .then(response => setDropdownPGCategoryOptions(response.response_data))
      .catch(err => console.error('Error fetching PG Category dropdown:', err));
  };

  const fetchDropdownItemGroupOptions = () => {
    fetchData('ItemApi', `/ItemGroup/${companyId}`, token)
      .then(response => setDropdownItemGroupOptions(response.response_data))
      .catch(err => console.error('Error fetching Item Group dropdown:', err));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateData('ItemApi', `${recordId}`, { ...formData, IsExcluding: Boolean(formData.IsExcluding), UpdatedBy: userId }, token)
      .then(() => history.push('/ItemIndex'))
      .catch(err => console.error('Error updating record:', err));
  };

  return (
    <div className="pg-container">
      {/* ── Header ── */}
      <div className="pg-header">
        <h5 className="pg-title">Item — Edit</h5>
        <div className="pg-header-actions">
          <div className="pg-action-row">
            <button className="pg-btn pg-btn-success" onClick={handleSubmit}>
              <DoneAllIcon /> Update
            </button>
            <Link to="/ItemIndex" className="pg-btn pg-btn-danger">
              <CloseIcon /> Cancel
            </Link>
          </div>
        </div>
      </div>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit}>
        <div className="pg-card">
          <div className="pg-form-grid pg-form-grid-2">
            <div className="pg-field">
              <label className="pg-label">Item Name <span className="req">*</span></label>
              <input className="pg-input" name="Name" value={formData.Name} onChange={handleChange} placeholder="Enter Name" />
            </div>
            <div className="pg-field">
              <label className="pg-label">Item Code <span className="req">*</span></label>
              <input className="pg-input" name="Code" value={formData.Code} onChange={handleChange} placeholder="Enter Code" />
            </div>
          </div>

          <div className="pg-form-grid pg-form-grid-4">
            <div className="pg-field">
              <label className="pg-label">Category <span className="req">*</span></label>
              <select className="pg-select" value={selectedCategory}
                onChange={e => { setSelectedCategory(e.target.value); setFormData(p => ({ ...p, CategoryId: e.target.value })); }}>
                <option value="">Select Category</option>
                {dropdownCategoryOptions.map(cat => <option key={cat.Id} value={cat.Id}>{cat.Name}</option>)}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label">PG Category <span className="req">*</span></label>
              <select className="pg-select" value={selectedPGCategory}
                onChange={e => { setSelectedPGCategory(e.target.value); setFormData(p => ({ ...p, PGCategoryId: e.target.value })); }}>
                <option value="">Select PG Category</option>
                {dropdownPGCategoryOptions.map(cat => <option key={cat.Id} value={cat.Id}>{cat.Name}</option>)}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label">Item Group <span className="req">*</span></label>
              <select className="pg-select" value={selectedItemGroup}
                onChange={e => { setSelectedItemGroup(e.target.value); setFormData(p => ({ ...p, ItemGroupId: e.target.value })); }}>
                <option value="">Select Item Group</option>
                {dropdownItemGroupOptions.map(cat => <option key={cat.Id} value={cat.Id}>{cat.Name}</option>)}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label">PF</label>
              <input className="pg-input" name="PF" value={formData.PF} onChange={handleChange} placeholder="Enter PF" />
            </div>
          </div>

          <div className="pg-form-grid pg-form-grid-4">
            <div className="pg-field">
              <label className="pg-label">ISG</label>
              <input className="pg-input" name="ISG" value={formData.ISG} onChange={handleChange} placeholder="Enter ISG" />
            </div>
            <div className="pg-field">
              <label className="pg-label">Purchase Rate</label>
              <input className="pg-input text-right" name="PurchaseRate" value={formData.PurchaseRate} onChange={handleChange} placeholder="0.00" />
            </div>
            <div className="pg-field">
              <label className="pg-label">MRP</label>
              <input className="pg-input text-right" name="MRP" value={formData.MRP} onChange={handleChange} placeholder="0.00" />
            </div>
            <div className="pg-field">
              <label className="pg-label">Discount %</label>
              <input className="pg-input text-right" name="DiscountPercentage" value={formData.DiscountPercentage} onChange={handleChange} placeholder="0.00" />
            </div>
          </div>

          <div className="pg-form-grid pg-form-grid-4">
            <div className="pg-field">
              <label className="pg-label">GST Rate</label>
              <input className="pg-input text-right" name="GSTRate" value={formData.GSTRate} onChange={handleChange} placeholder="0.00" />
            </div>
            <div className="pg-field">
              <label className="pg-label">HSN Code</label>
              <input className="pg-input" name="HSNCode" value={formData.HSNCode} onChange={handleChange} placeholder="Enter HSN Code" />
            </div>
            <div className="pg-field">
              <label className="pg-checkbox-label">
                <input
                  type="checkbox"
                  name="IsExcluding"
                  checked={formData.IsExcluding === 1 || formData.IsExcluding === true}
                  onChange={handleChange}
                />
                Is Excluding
              </label>
            </div>
          </div>
        </div>
      </form>

      <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
    </div>
  );
};

export default ItemEdit;
